import React, { useRef, useState, useEffect } from 'react';
import { Filter, ChevronDown } from 'lucide-react';

const FilterSection = ({ filters, setFilters, attendantsList = [] }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      let next = { ...prev, [key]: value };
      if (key === 'apenasExpediente' && value) {
        next.apenasForaExpediente = false;
      }
      if (key === 'apenasForaExpediente' && value) {
        next.apenasExpediente = false;
      }
      return next;
    });
  };

  // [] = todos selecionados; array com nomes = apenas esses
  const atendentes = filters.atendentes || [];
  const todosAtivos = atendentes.length === 0;
  const isChecked = (nome) => todosAtivos || atendentes.includes(nome);

  const toggleAtendente = (nome) => {
    if (todosAtivos) {
      // desmarca um: seleciona todos exceto este
      handleFilterChange('atendentes', attendantsList.filter(a => a !== nome));
    } else {
      const next = atendentes.includes(nome)
        ? atendentes.filter(a => a !== nome)
        : [...atendentes, nome];
      // se todos estão marcados, volta ao estado "todos" (array vazio)
      handleFilterChange('atendentes', next.length === attendantsList.length ? [] : next);
    }
  };

  const selecionarTodos = () => handleFilterChange('atendentes', []);

  const labelDropdown = todosAtivos
    ? `Todos (${attendantsList.length})`
    : `${atendentes.length} de ${attendantsList.length}`;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="h-5 w-5 text-orange-500" />
        <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Origem</label>
          <input
            type="text"
            placeholder="Filtrar por origem..."
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            value={filters.origem}
            onChange={(e) => handleFilterChange('origem', e.target.value)}
          />
        </div>

        <div ref={dropdownRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Atendentes</label>
          <button
            type="button"
            className="w-full p-2 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            onClick={() => setDropdownOpen(o => !o)}
          >
            <span className={todosAtivos ? 'text-gray-500' : 'text-gray-800 font-medium'}>
              {labelDropdown}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">{attendantsList.length} atendentes</span>
                {!todosAtivos && (
                  <button
                    type="button"
                    className="text-xs text-orange-500 hover:text-orange-700 font-medium"
                    onClick={selecionarTodos}
                  >
                    Selecionar todos
                  </button>
                )}
              </div>
              {attendantsList.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">
                  Nenhum atendente disponível
                </div>
              ) : (
                attendantsList.map(nome => (
                  <label
                    key={nome}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-orange-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      checked={isChecked(nome)}
                      onChange={() => toggleAtendente(nome)}
                    />
                    <span className="text-gray-700 truncate">{nome}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="com_resposta">Com resposta</option>
            <option value="sem_resposta">Sem resposta</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data início</label>
          <input
            type="date"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            value={filters.dataInicio || ''}
            onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data fim</label>
          <input
            type="date"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            value={filters.dataFim || ''}
            onChange={(e) => handleFilterChange('dataFim', e.target.value)}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dias Úteis</label>
          <div className="flex items-center h-10">
            <input
              type="checkbox"
              id="ignorarFimSemana"
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              checked={filters.ignorarFimSemana}
              onChange={(e) => handleFilterChange('ignorarFimSemana', e.target.checked)}
            />
            <label htmlFor="ignorarFimSemana" className="ml-2 text-sm text-gray-700">
              Ignorar fim de semana
            </label>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expediente</label>
          <div className="flex items-center h-10">
            <input
              type="checkbox"
              id="apenasExpediente"
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              checked={filters.apenasExpediente}
              onChange={(e) => handleFilterChange('apenasExpediente', e.target.checked)}
            />
            <label htmlFor="apenasExpediente" className="ml-2 text-sm text-gray-700">
              Apenas 08h-20h
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
